import type { WidgetConfig } from '../types';
import widgetStyles from '../widget.css?inline';
import { createWidgetApi } from './api';
import { resolveConfig, widgetConfigUrl } from './config';
import { mount, setWidgetStyles } from './dom';
import { isLoaded, markLoaded } from './guard';
import {
    drainQueue,
    getOrCreateQueue,
    installCommandStub,
    installLiveDispatcher,
} from './queue';

const LOG_PREFIX = '[OrderPilot]';
const PREFLIGHT_TIMEOUT_MS = 8000;

export function boot(win: Window = window): void {
    try {
        if (isLoaded(win)) {
            console.warn(`${LOG_PREFIX} Snippet included twice — ignoring.`);

            return;
        }

        const script =
            win.document.currentScript instanceof HTMLScriptElement
                ? win.document.currentScript
                : null;

        const result = resolveConfig(
            script?.dataset.widgetKey,
            script?.dataset.apiUrl,
            script?.src,
            win.orderPilotSettings,
        );

        if (!result.ok) {
            console.warn(`${LOG_PREFIX} ${result.reason}`);

            return;
        }

        installCommandStub(win);

        const start = async (): Promise<void> => {
            try {
                // Unknown keys, disabled widgets, and domain mismatches 404 here — stay silent.
                if (!(await isAllowed(result.config))) {
                    return;
                }

                markLoaded(win);
                setWidgetStyles(widgetStyles);

                const dom = mount(win.document, result.config);
                const [api, emit] = createWidgetApi(dom);

                dom.bubble.addEventListener('click', () => api.toggle());

                drainQueue(getOrCreateQueue(win), api);
                installLiveDispatcher(win, api);
                emit('ready');
            } catch (error) {
                console.warn(`${LOG_PREFIX} Failed to start:`, error);
            }
        };

        if (win.document.readyState === 'loading') {
            win.document.addEventListener(
                'DOMContentLoaded',
                () => void start(),
                {
                    once: true,
                },
            );
        } else {
            void start();
        }
    } catch (error) {
        console.warn(`${LOG_PREFIX} Failed to start:`, error);
    }
}

async function isAllowed(config: WidgetConfig): Promise<boolean> {
    try {
        const response = await fetch(widgetConfigUrl(config), {
            headers: { Accept: 'application/json' },
            signal: AbortSignal.timeout(PREFLIGHT_TIMEOUT_MS),
        });

        return response.ok;
    } catch {
        return false;
    }
}
